import React, { MouseEventHandler } from 'react';
import styled, { css } from 'styled-components';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition, IconProp } from '@fortawesome/fontawesome-svg-core';
import { COLORS } from '../settings/variables/Colors';
import { spinAnimation } from '../../components/LoadingSpinner';
import TrueImpactText from '../base/TrueImpactText';
import { SPACERS } from '../settings/variables/Spacers';

export interface TrueImpactIconButtonProps {
  label?: React.ReactNode;
  id?: string;
  text?: string;
  size?: number;
  padding?: number;
  spin?: boolean;
  disabled?: boolean;
  color?: keyof typeof COLORS;
  backgroundColor?: keyof typeof COLORS;
  icon: IconProp | IconDefinition;
  className?: string;
  style?: React.CSSProperties;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick?: (() => unknown) | MouseEventHandler<any>;
}

const StyledIconButton = styled(IconButton)<{
  isCircled: boolean;
  spin: TrueImpactIconButtonProps['spin'];
  iconSize: TrueImpactIconButtonProps['size'];
  padding: TrueImpactIconButtonProps['padding'];
  iconColor: TrueImpactIconButtonProps['color'];
  backgroundColor?: TrueImpactIconButtonProps['backgroundColor'];
}>`
  ${({ isCircled }) =>
    isCircled
      ? ''
      : `border-radius: ${SPACERS.extraTiny} !important; padding-right: ${SPACERS.tiny} !important`};
  svg {
    width: ${({ iconSize }) => iconSize}px !important;
    height: ${({ iconSize }) => iconSize}px !important;
    color: ${({ iconColor }) => (iconColor ? COLORS[iconColor] : 'inherit')};
    padding: ${({ padding }) => padding}px;
    ${({ spin }) =>
      spin
        ? css`
            animation: ${spinAnimation} 1s linear infinite;
          `
        : ''}
    path {
      ${({ iconColor }) =>
        iconColor
          ? `fill: ${COLORS[iconColor]} !important; stroke: ${COLORS[iconColor]} !important`
          : ''}
    }
  }
  background-color: ${({ backgroundColor }) =>
    backgroundColor ? `${COLORS[backgroundColor]} !important` : 'inherit'};
  &:disabled svg {
    color: inherit;
  }
`;

const TrueImpactIconButton: React.FC<TrueImpactIconButtonProps> = ({
  id,
  label,
  text,
  icon,
  color,
  backgroundColor,
  spin,
  size = 15,
  padding = 4,
  disabled,
  onClick,
  className,
  style,
}) => {
  const Component = (
    <StyledIconButton
      id={id}
      iconColor={color}
      className={className}
      padding={padding}
      iconSize={size}
      disabled={disabled}
      onClick={onClick}
      spin={spin}
      style={style}
      isCircled={!text}
      backgroundColor={backgroundColor}
    >
      <FontAwesomeIcon icon={icon as IconProp} />
      {text && (
        <TrueImpactText variant="body-small" color={color}>
          {text}
        </TrueImpactText>
      )}
    </StyledIconButton>
  );

  if (!label) return Component;

  return (
    <Tooltip title={label} color={color} arrow={true}>
      {Component}
    </Tooltip>
  );
};

export default TrueImpactIconButton;
